import { ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import PropTypes from 'prop-types'
import UserPool from '../pages/auth/UserPool'

/**
 * Props for the AuthGuard component.
 */
interface AuthGuardProps {
    /** The child components to be rendered if the user is authenticated */
    children: ReactNode
}

/**
 * AuthGuard component ensures that only authenticated users can access protected routes.
 * If a user is not authenticated, they are redirected to the sign-in page.
 *
 * @param {AuthGuardProps} props - The properties for the AuthGuard component.
 * @returns {JSX.Element} The wrapped children if authenticated, otherwise redirects to sign-in.
 */
export default function AuthGuard({ children }: AuthGuardProps): JSX.Element {
    const currentUser = UserPool.getCurrentUser()
    const location = useLocation()

    const isDashboardRoute = location.pathname === '/'
    const hasNameAndVersionParams = new URLSearchParams(location.search).has('name') && new URLSearchParams(location.search).has('version')

    const commercialUrl = '/commercial-licenses'
    const studentUrl = '/student-licenses'
    const trainingUrl = '/training-courses'
    const evaluationUrl = '/evaluation-licenses'

    const isCommercialLink = location.pathname === commercialUrl
    const isStudentLink = location.pathname === studentUrl
    const isTrainingLink = location.pathname === trainingUrl
    const isEvaluationLink = location.pathname === evaluationUrl

    if (!currentUser) {
        if (isDashboardRoute && hasNameAndVersionParams) {
            localStorage.setItem('previousUrl', location.pathname + location.search)
        }
        const mode = isCommercialLink
            ? 'commercial'
            : isStudentLink
              ? 'academic'
              : isTrainingLink
                ? 'training'
                : isEvaluationLink
                  ? 'evaluation'
                  : null
        return (
            <Navigate
                to={'/auth/sign-in'}
                state={{
                    previousUrl: `${location?.pathname}${location?.search}`,
                    mode: mode
                }}
                replace
            />
        )
    }

    return <>{children}</>
}

AuthGuard.propTypes = {
    children: PropTypes.node.isRequired
}
